import clsx from 'clsx';
import {
  Archive,
  Bell,
  Cable,
  ChevronDown,
  ClipboardList,
  LayoutGrid,
  LogOut,
  Menu,
  Moon,
  Plus,
  Settings2,
  ShieldCheck,
  Signpost,
  Siren,
  Sun,
  UserRound,
  Users,
  Wand2,
  X,
} from 'lucide-react';
import { Suspense, useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { fr } from '@/i18n/fr';
import { api } from '@/lib/api';
import { useApi, useCan } from '@/lib/hooks';
import { useSession } from '@/lib/session';
import type { Alert, Dashboard } from '@/lib/types';
import { Spinner } from '../ui/core';
import { HealthGlyph } from '../ui/status';

/** Main sections, shown in the dark pill on wide screens. */
function useSections() {
  const allowed = useCan();
  const alerts = useApi<{ alert: Alert }[]>('/alerts?status=open', { refetchInterval: 30_000 });
  const critical = alerts.data?.filter((a) => a.alert.severity === 'critical').length ?? 0;
  return [
    { to: '/', end: true, label: fr.nav.dashboard, icon: <LayoutGrid /> },
    { to: '/alerts', label: fr.nav.alerts, icon: <Bell />, badge: critical },
    { to: '/incidents', label: fr.nav.incidents, icon: <Siren /> },
    { to: '/security', label: fr.nav.security, icon: <ShieldCheck /> },
    { to: '/backups', label: fr.nav.backups, icon: <Archive /> },
    { to: '/connectors', label: fr.nav.connectors, icon: <Cable /> },
    ...(allowed('audit.read') ? [{ to: '/audit', label: 'Audit', icon: <ClipboardList /> }] : []),
  ] as { to: string; end?: boolean; label: string; icon: ReactNode; badge?: number }[];
}

function PillNav() {
  const sections = useSections();
  return (
    <nav aria-label="Navigation principale" className="hidden items-center gap-1 rounded-full bg-panel p-1.5 shadow-[0_18px_40px_-20px_rgba(0,0,0,.55)] ring-1 ring-white/[0.06] xl:flex">
      {sections.map((s) => (
        <NavLink
          key={s.to}
          to={s.to}
          end={s.end}
          className={({ isActive }) =>
            clsx(
              'relative flex h-9 items-center gap-1.5 rounded-full px-4 text-[0.8125rem] font-semibold whitespace-nowrap transition-colors',
              isActive ? 'bg-lime text-lime-ink' : 'text-panel-muted hover:text-panel-ink',
            )
          }
        >
          {({ isActive }) => (
            <>
              {isActive && <span aria-hidden className="size-1.5 rounded-full bg-lime-ink" />}
              {s.label}
              {s.badge ? <span className="ml-0.5 rounded-full bg-bad px-1.5 text-[0.6875rem] leading-4 font-bold text-white">{s.badge}</span> : null}
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

function useOutside(ref: React.RefObject<HTMLElement | null>, onOutside: () => void) {
  useEffect(() => {
    const h = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && onOutside();
    const k = (e: KeyboardEvent) => e.key === 'Escape' && onOutside();
    document.addEventListener('mousedown', h);
    document.addEventListener('keydown', k);
    return () => {
      document.removeEventListener('mousedown', h);
      document.removeEventListener('keydown', k);
    };
  }, [ref, onOutside]);
}

/** Dropdown listing every SaaS with its health. */
function ProjectsMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useOutside(ref, () => setOpen(false));
  const allowed = useCan();
  const navigate = useNavigate();
  const dash = useApi<Dashboard>('/dashboard', { refetchInterval: 30_000 });
  const projects = dash.data?.projects ?? [];
  const critical = projects.reduce((n, p) => n + p.alerts.critical, 0);
  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(!open)} aria-expanded={open} aria-haspopup="menu" className="flex h-10 items-center gap-2 rounded-full bg-surface-solid px-4 text-[0.8125rem] font-semibold ring-1 ring-hairline hover:bg-surface-2">
        SaaS
        <span className="num rounded-full bg-ink/[0.07] px-1.5 text-caption">{projects.length}</span>
        {critical > 0 && <span className="size-2 rounded-full bg-bad" aria-label={`${critical} alerte(s) critique(s)`} />}
        <ChevronDown className="size-3.5 text-muted" />
      </button>
      {open && (
        <div role="menu" className="absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-[22px] bg-surface-solid p-2 shadow-pop">
          {projects.length === 0 && <p className="px-3 py-3 text-caption text-muted">Aucun SaaS pour l’instant.</p>}
          {projects.map((p) => (
            <NavLink key={p.id} to={`/projects/${p.slug}`} onClick={() => setOpen(false)} className={({ isActive }) => clsx('flex items-center gap-3 rounded-2xl px-3 py-2.5 hover:bg-surface-2', isActive && 'bg-lime-soft')}>
              <HealthGlyph health={p.health} size={12} />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-semibold">{p.name}</span>
                <span className="block truncate text-caption text-muted">{fr.health[p.health]} · {fr.criticality[p.criticality].toLowerCase()}</span>
              </span>
              {p.alerts.critical > 0 && <span className="rounded-full bg-bad px-1.5 text-[0.6875rem] leading-4 font-bold text-white">{p.alerts.critical}</span>}
            </NavLink>
          ))}
          {allowed('project.create') && (
            <button onClick={() => { setOpen(false); navigate('/projects/new'); }} className="mt-1 flex w-full items-center gap-2 rounded-2xl px-3 py-2.5 text-[0.8125rem] font-semibold hover:bg-surface-2">
              <span className="flex size-6 items-center justify-center rounded-full bg-lime text-lime-ink"><Plus className="size-3.5" /></span>
              Ajouter un SaaS
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function IconLink({ to, label, children, badge }: { to: string; label: string; children: ReactNode; badge?: number }) {
  return (
    <NavLink to={to} aria-label={label} title={label} className={({ isActive }) => clsx('relative flex size-10 items-center justify-center rounded-full ring-1 transition-colors [&>svg]:size-[18px]', isActive ? 'bg-panel text-panel-ink ring-panel' : 'bg-surface-solid text-ink ring-hairline hover:bg-surface-2')}>
      {children}
      {badge ? <span className="absolute -top-0.5 -right-0.5 min-w-4 rounded-full bg-bad px-1 text-center text-[0.625rem] leading-4 font-bold text-white">{badge}</span> : null}
    </NavLink>
  );
}

function useTheme() {
  const [theme, setTheme] = useState<'light' | 'dark' | 'system'>(() => {
    try {
      return (localStorage.getItem('sm-theme') as 'light' | 'dark' | null) ?? 'system';
    } catch {
      return 'system';
    }
  });
  useEffect(() => {
    if (theme === 'system') document.documentElement.removeAttribute('data-theme');
    else document.documentElement.setAttribute('data-theme', theme);
    try {
      if (theme === 'system') localStorage.removeItem('sm-theme');
      else localStorage.setItem('sm-theme', theme);
    } catch {
      /* private mode */
    }
  }, [theme]);
  const dark = theme === 'dark' || (theme === 'system' && typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  return { dark, toggle: () => setTheme(dark ? 'light' : 'dark') };
}

function AccountMenu() {
  const { me } = useSession();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useOutside(ref, () => setOpen(false));
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { dark, toggle } = useTheme();
  const initials = me?.user.name.split(/\s+/).map((s) => s[0]).slice(0, 2).join('').toUpperCase();
  async function logout() {
    await api.post('/auth/logout').catch(() => undefined);
    qc.clear();
    navigate('/login');
  }
  const item = 'flex w-full items-center gap-2.5 rounded-2xl px-3 py-2.5 text-[0.8125rem] font-medium hover:bg-surface-2 [&>svg]:size-4 [&>svg]:text-muted';
  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(!open)} aria-label="Mon compte" aria-expanded={open} className="flex size-10 items-center justify-center rounded-full bg-panel text-[0.75rem] font-bold text-lime ring-2 ring-lime/60">
        {initials}
      </button>
      {open && (
        <div role="menu" className="absolute right-0 z-50 mt-2 w-64 rounded-[22px] bg-surface-solid p-2 shadow-pop">
          <div className="px-3 pt-2 pb-3">
            <p className="truncate font-semibold">{me?.user.name}</p>
            <p className="truncate text-caption text-muted">{me?.user.email} · {me && fr.role[me.user.role]}</p>
          </div>
          <NavLink to="/account" onClick={() => setOpen(false)} className={item}><UserRound />Mon compte</NavLink>
          <NavLink to="/roadmap" onClick={() => setOpen(false)} className={item}><Signpost />{fr.nav.roadmap}</NavLink>
          <button onClick={toggle} className={item}>{dark ? <Sun /> : <Moon />}{dark ? 'Thème clair' : 'Thème sombre'}</button>
          <button onClick={logout} className={clsx(item, 'text-bad [&>svg]:text-bad')}><LogOut />Se déconnecter</button>
        </div>
      )}
    </div>
  );
}

function TopBar({ onMenu }: { onMenu: () => void }) {
  const allowed = useCan();
  const setup = useApi<{ remaining: string[] }>(allowed('settings.manage') ? '/setup' : null, { refetchInterval: 60_000 });
  return (
    <header className="sticky top-0 z-40 px-4 pt-4 sm:px-6">
      <div className="mx-auto flex max-w-[1320px] items-center gap-3">
        <NavLink to="/" className="flex shrink-0 items-center gap-2.5 pr-2" aria-label={fr.app.name}>
          <img src="/favicon.svg" alt="" className="size-9 rounded-[11px] ring-1 ring-white/10" />
          <span className="hidden text-[0.9375rem] font-bold tracking-tight sm:block">{fr.app.name}</span>
        </NavLink>
        <div className="flex flex-1 justify-center">
          <PillNav />
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <ProjectsMenu />
          {allowed('settings.manage') && (
            <IconLink to="/setup" label="Configuration" badge={setup.data?.remaining.length || undefined}>
              <Wand2 />
            </IconLink>
          )}
          {allowed('users.manage') && (
            <span className="hidden sm:contents">
              <IconLink to="/users" label={fr.nav.users}><Users /></IconLink>
            </span>
          )}
          {allowed('audit.read') && (
            <span className="hidden sm:contents">
              <IconLink to="/settings" label={fr.nav.settings}><Settings2 /></IconLink>
            </span>
          )}
          <AccountMenu />
          <button onClick={onMenu} aria-label="Ouvrir la navigation" className="flex size-10 items-center justify-center rounded-full bg-surface-solid ring-1 ring-hairline xl:hidden">
            <Menu className="size-[18px]" />
          </button>
        </div>
      </div>
    </header>
  );
}

function MobileNav({ onClose }: { onClose: () => void }) {
  const sections = useSections();
  const allowed = useCan();
  const extra = [
    ...(allowed('settings.manage') ? [{ to: '/setup', label: 'Configuration', icon: <Wand2 /> }] : []),
    ...(allowed('users.manage') ? [{ to: '/users', label: fr.nav.users, icon: <Users /> }] : []),
    ...(allowed('audit.read') ? [{ to: '/settings', label: fr.nav.settings, icon: <Settings2 /> }] : []),
  ];
  return (
    <div className="fixed inset-0 z-50 xl:hidden" role="dialog" aria-modal="true" aria-label="Navigation">
      <div className="absolute inset-0 bg-[rgba(18,20,23,.4)] backdrop-blur-sm" onClick={onClose} />
      <nav className="absolute inset-x-3 top-3 rounded-[28px] bg-panel p-3 text-panel-ink shadow-pop" onClick={(e) => (e.target as HTMLElement).closest('a') && onClose()}>
        <div className="mb-2 flex items-center justify-between px-2">
          <span className="font-bold">{fr.app.name}</span>
          <button onClick={onClose} aria-label="Fermer" className="flex size-9 items-center justify-center rounded-full bg-panel-2"><X className="size-4" /></button>
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          {[...sections, ...extra].map((s) => (
            <NavLink key={s.to} to={s.to} end={'end' in s ? s.end : false} className={({ isActive }) => clsx('flex items-center gap-2.5 rounded-2xl px-3.5 py-3 text-[0.8125rem] font-semibold [&>svg]:size-4', isActive ? 'bg-lime text-lime-ink' : 'bg-panel-2 text-panel-ink')}>
              {s.icon}
              {s.label}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}

export function Shell() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  return (
    <div className="min-h-dvh">
      <TopBar onMenu={() => setOpen(true)} />
      {open && <MobileNav onClose={() => setOpen(false)} />}
      <main key={location.pathname.split('/').slice(0, 3).join('/')} className="mx-auto w-full max-w-[1320px] px-4 pt-8 pb-16 sm:px-6">
        <Suspense fallback={<Spinner />}>
          <Outlet />
        </Suspense>
      </main>
    </div>
  );
}
